import React from "react";
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AddCustomConventionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function AddCustomConventionDialog({ open, onOpenChange, onSuccess }: AddCustomConventionDialogProps) {
  const [squareSuffix, setSquareSuffix] = useState('');
  const [verticalSuffix, setVerticalSuffix] = useState('');
  const [landscapeSuffix, setLandscapeSuffix] = useState('');
  const [includeTriple, setIncludeTriple] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const handleSave = async () => {
    if (!squareSuffix.trim() || !verticalSuffix.trim()) {
      toast({
        title: 'Missing fields',
        description: 'Square and Vertical suffixes are required',
        variant: 'destructive'
      });
      return;
    }

    if (includeTriple && !landscapeSuffix.trim()) {
      toast({
        title: 'Missing field',
        description: 'Landscape suffix is required for triples',
        variant: 'destructive'
      });
      return;
    }

    setSaving(true);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({ title: 'Error', description: 'Not authenticated', variant: 'destructive' });
      setSaving(false);
      return;
    }

    const label = includeTriple 
      ? `${squareSuffix} / ${verticalSuffix} / ${landscapeSuffix}`
      : `${squareSuffix} / ${verticalSuffix}`;

    const { error } = await supabase.from('naming_conventions').insert({
      user_id: user.id,
      label,
      square_suffix: squareSuffix.trim(),
      vertical_suffix: verticalSuffix.trim(),
      landscape_suffix: includeTriple ? landscapeSuffix.trim() : null
    });

    setSaving(false);

    if (error) {
      toast({
        title: 'Error saving convention',
        description: error.message,
        variant: 'destructive'
      });
    } else {
      toast({
        title: 'Convention saved',
        description: 'Your custom naming convention has been added'
      });
      setSquareSuffix('');
      setVerticalSuffix('');
      setLandscapeSuffix('');
      setIncludeTriple(false);
      onSuccess();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Custom Naming Convention</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="square">Square Suffix (1:1 or 4:5)</Label>
            <Input
              id="square"
              placeholder="e.g., 1x1, square, 4x5"
              value={squareSuffix}
              onChange={(e) => setSquareSuffix(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="vertical">Vertical Suffix (9:16)</Label>
            <Input
              id="vertical"
              placeholder="e.g., 9x16, vertical, story"
              value={verticalSuffix}
              onChange={(e) => setVerticalSuffix(e.target.value)}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="include-triple">Include Landscape (16:9) for Triples</Label>
            <Switch
              id="include-triple"
              checked={includeTriple}
              onCheckedChange={setIncludeTriple}
            />
          </div>

          {includeTriple && (
            <div className="space-y-2">
              <Label htmlFor="landscape">Landscape Suffix (16:9)</Label>
              <Input
                id="landscape"
                placeholder="e.g., 16x9, landscape, wide"
                value={landscapeSuffix}
                onChange={(e) => setLandscapeSuffix(e.target.value)}
              />
            </div>
          )}

          <div className="rounded-lg bg-muted p-3 space-y-2">
            <p className="text-sm font-medium">Preview:</p>
            <p className="text-sm text-muted-foreground">
              {squareSuffix || '[square]'} / {verticalSuffix || '[vertical]'}
              {includeTriple && ` / ${landscapeSuffix || '[landscape]'}`}
            </p>
            <p className="text-xs text-muted-foreground">
              Example: video_{squareSuffix || 'suffix'}.mp4 will match with video_{verticalSuffix || 'suffix'}.mp4
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Convention'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
